import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { recoveryWalletApi } from '../../utils/recoveryWalletApi';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  Button,
  Alert,
  Stack,
  Divider,
  Skeleton,
  useTheme,
  Chip,
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SavingsIcon from '@mui/icons-material/Savings';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';

const PaymentOptions = ({ 
  totalAmount, 
  onPaymentMethodChange, 
  selectedMethod = 'tokens',
  onPaymentBreakdownChange 
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const borderRadius = '25px';
  const compactRadius = '15px';
  const headingColor = theme.palette.mode === 'dark' ? '#ffffff' : '#000000';
  const mutedColor = theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)';
  const [mainWalletBalance, setMainWalletBalance] = useState(0);
  const [recoveryWalletBalance, setRecoveryWalletBalance] = useState(0);
  const [paymentBreakdown, setPaymentBreakdown] = useState({
    recoveryWallet: 0,
    mainWallet: 0,
    onlinePayment: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id || user?._id) {
      fetchWalletBalances();
    } else {
      const timeout = setTimeout(() => {
        if (!user?.id && !user?._id) {
          setLoading(false);
        }
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [user?._id]);

  useEffect(() => {
    if (!loading && mainWalletBalance >= 0 && recoveryWalletBalance >= 0) {
      calculateOptimalBreakdown();
      const totalAvailable = recoveryWalletBalance + mainWalletBalance;
      if (totalAvailable >= totalAmount) {
        onPaymentMethodChange('mixed');
      } else if (recoveryWalletBalance >= totalAmount) {
        onPaymentMethodChange('recovery_wallet');
      } else if (mainWalletBalance >= totalAmount) {
        onPaymentMethodChange('tokens');
      } else {
        onPaymentMethodChange('mixed');
      }
    }
  }, [loading, mainWalletBalance, recoveryWalletBalance, totalAmount]);

  useEffect(() => {
    if (selectedMethod === 'mixed') {
      calculateOptimalBreakdown();
    } else if (selectedMethod === 'recovery_wallet') {
      setPaymentBreakdown({
        recoveryWallet: totalAmount,
        mainWallet: 0,
        onlinePayment: 0
      });
    } else if (selectedMethod === 'tokens') {
      setPaymentBreakdown({
        recoveryWallet: 0,
        mainWallet: totalAmount,
        onlinePayment: 0
      });
    }
  }, [selectedMethod, totalAmount, recoveryWalletBalance, mainWalletBalance]);

  useEffect(() => {
    if (onPaymentBreakdownChange) {
      onPaymentBreakdownChange(paymentBreakdown);
    }
  }, [paymentBreakdown, onPaymentBreakdownChange]);

  const fetchWalletBalances = async () => {
    try {
      setLoading(true);
      const userId = user?.id || user?._id;
      const mainBalance = user?.tokenBalance || user?.wallet?.tokenBalance || 0;
      setMainWalletBalance(mainBalance);
      
      const directRecoveryBalance = user?.recoveryBalance;
      if (typeof directRecoveryBalance === 'number') {
        setRecoveryWalletBalance(directRecoveryBalance);
      } else {
        try {
          const recoveryResponse = await recoveryWalletApi.getWallet(userId);
          if (recoveryResponse?.data?.success) {
            setRecoveryWalletBalance(recoveryResponse.data.data.balance || 0);
          } else {
            setRecoveryWalletBalance(0);
          }
        } catch (recoveryError) {
          setRecoveryWalletBalance(0);
        }
      }
    } catch (error) {
      setMainWalletBalance(user?.tokenBalance || user?.wallet?.tokenBalance || 0);
      setRecoveryWalletBalance(user?.recoveryBalance || 0);
    } finally {
      setLoading(false);
    }
  };

  const calculateOptimalBreakdown = () => {
    let remaining = totalAmount;
    let recovery = 0;
    let main = 0;
    let online = 0;

    if (recoveryWalletBalance > 0) {
      recovery = Math.min(remaining, recoveryWalletBalance);
      remaining -= recovery;
    }

    if (remaining > 0 && mainWalletBalance > 0) {
      main = Math.min(remaining, mainWalletBalance);
      remaining -= main;
    }

    online = remaining;

    setPaymentBreakdown({
      recoveryWallet: recovery,
      mainWallet: main,
      onlinePayment: online
    });
  };

  const updateBreakdownValue = (field, value) => {
    const numValue = Math.max(0, Number(value) || 0);
    setPaymentBreakdown(prev => ({
      ...prev,
      [field]: numValue
    }));
  };

  const formatTokens = (amount) => {
    return `${new Intl.NumberFormat('en-US').format(amount || 0)} TND`;
  };

  const getTotalBreakdown = () => {
    return paymentBreakdown.recoveryWallet + paymentBreakdown.mainWallet + paymentBreakdown.onlinePayment;
  };

  const isBreakdownValid = () => {
    return Math.abs(getTotalBreakdown() - totalAmount) < 0.01;
  };

  const canUseRecoveryWallet = () => recoveryWalletBalance > 0;
  const canUseMainWallet = () => mainWalletBalance > 0;
  const canAffordWithRecoveryOnly = () => recoveryWalletBalance >= totalAmount;
  const canAffordWithMainOnly = () => mainWalletBalance >= totalAmount;
  const canAffordWithBothWallets = () => (recoveryWalletBalance + mainWalletBalance) >= totalAmount;

  if (loading) {
    return (
      <Paper sx={{ p: 3, borderRadius, bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#fafafa' }}>
        <Stack spacing={2}>
          <Skeleton variant="text" width="60%" height={30} />
          <Skeleton variant="rectangular" height={60} sx={{ borderRadius: compactRadius }} />
          <Skeleton variant="rectangular" height={60} sx={{ borderRadius: compactRadius }} />
        </Stack>
      </Paper>
    );
  }

  if (!user?.id && !user?._id) {
    return (
      <Alert severity="warning" variant="outlined" sx={{ borderRadius: compactRadius }}>
        Please log in to see payment options.
      </Alert>
    );
  }

  return (
    <Stack spacing={3}>
      {/* Automatic Payment Breakdown Summary */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius,
          background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.1), rgba(6, 182, 212, 0.1))',
          border: '1px solid',
          borderColor: 'rgba(147, 51, 234, 0.3)',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
          <AutoFixHighIcon color="secondary" />
          <Typography variant="h6" fontWeight={600} color={headingColor}>
            Automatic Payment Distribution
          </Typography>
        </Stack>
        
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                textAlign: 'center',
                bgcolor: 'rgba(6, 182, 212, 0.1)',
                border: '1px solid',
                borderColor: 'rgba(6, 182, 212, 0.3)',
                borderRadius: compactRadius,
              }}
            >
              <Typography variant="caption" color="info.main" fontWeight={600}>
                Recovery Wallet
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {formatTokens(paymentBreakdown.recoveryWallet)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={4}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                textAlign: 'center',
                bgcolor: 'rgba(34, 197, 94, 0.1)',
                border: '1px solid',
                borderColor: 'rgba(34, 197, 94, 0.3)',
                borderRadius: compactRadius,
              }}
            >
              <Typography variant="caption" color="success.main" fontWeight={600}>
                Main Wallet
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {formatTokens(paymentBreakdown.mainWallet)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={4}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                textAlign: 'center',
                bgcolor: 'rgba(249, 115, 22, 0.1)',
                border: '1px solid',
                borderColor: 'rgba(249, 115, 22, 0.3)',
                borderRadius: compactRadius,
              }}
            >
              <Typography variant="caption" color="warning.main" fontWeight={600}>
                Online Payment
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                {new Intl.NumberFormat('en-US').format(paymentBreakdown.onlinePayment || 0)} TND
              </Typography>
            </Paper>
          </Grid>
        </Grid>
        
        <Stack direction="row" justifyContent="center" spacing={1} mt={2}>
          <Typography variant="body2" color="text.secondary">Total:</Typography>
          <Typography variant="body2" fontWeight={700}>
            {new Intl.NumberFormat('en-US').format(totalAmount || 0)} TND
          </Typography>
        </Stack>
      </Paper>

      {/* Payment Method Selection */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius,
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#ffffff',
          border: '1px solid',
          borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
        }}
      >
        <Typography
          variant="h6"
          fontWeight={600}
          gutterBottom
          sx={{ display: 'flex', alignItems: 'center', gap: 1, color: headingColor }}
        >
          <CreditCardIcon /> Payment Options
        </Typography>
        
        <Alert severity="info" sx={{ mb: 3, borderRadius: compactRadius }}>
          <Typography variant="body2">
            <strong>Smart Payment:</strong> Automatically uses Recovery Wallet first, then Main Wallet, then online payment.
          </Typography>
        </Alert>

        <RadioGroup
          value={selectedMethod}
          onChange={(e) => onPaymentMethodChange(e.target.value)}
        >
          <Stack spacing={2}>
            {canUseRecoveryWallet() && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: compactRadius,
                  border: '1px solid',
                  borderColor: selectedMethod === 'recovery_wallet' ? 'info.main' : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
                  bgcolor: selectedMethod === 'recovery_wallet' ? 'rgba(6, 182, 212, 0.08)' : 'transparent',
                  opacity: !canAffordWithRecoveryOnly() ? 0.7 : 1,
                }}
              >
                <FormControlLabel
                  value="recovery_wallet"
                  control={<Radio disabled={!canAffordWithRecoveryOnly()} />}
                  label={
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600} color={headingColor}>Recovery Wallet</Typography>
                      <Typography variant="body2" sx={{ color: mutedColor }}>
                        Balance: {formatTokens(recoveryWalletBalance)} 🪙
                        {!canAffordWithRecoveryOnly() && (
                          <Typography component="span" color="error.main" ml={1} variant="caption">
                            (Insufficient)
                          </Typography>
                        )}
                      </Typography>
                    </Box>
                  }
                  sx={{ width: '100%', m: 0 }}
                />
              </Paper>
            )}

            {canUseMainWallet() && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: compactRadius,
                  border: '1px solid',
                  borderColor: selectedMethod === 'tokens' ? 'success.main' : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
                  bgcolor: selectedMethod === 'tokens' ? 'rgba(34, 197, 94, 0.08)' : 'transparent',
                  opacity: !canAffordWithMainOnly() ? 0.7 : 1,
                }}
              >
                <FormControlLabel
                  value="tokens"
                  control={<Radio disabled={!canAffordWithMainOnly()} />}
                  label={
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600} color={headingColor}>Main Wallet</Typography>
                      <Typography variant="body2" sx={{ color: mutedColor }}>
                        Balance: {formatTokens(mainWalletBalance)} 🪙
                        {!canAffordWithMainOnly() && (
                          <Typography component="span" color="error.main" ml={1} variant="caption">
                            (Insufficient)
                          </Typography>
                        )}
                      </Typography>
                    </Box>
                  }
                  sx={{ width: '100%', m: 0 }}
                />
              </Paper>
            )}

            {(canUseRecoveryWallet() || canUseMainWallet()) && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: compactRadius,
                  border: '1px solid',
                  borderColor: selectedMethod === 'mixed' ? 'secondary.main' : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
                  bgcolor: selectedMethod === 'mixed' ? 'rgba(147, 51, 234, 0.08)' : 'transparent',
                }}
              >
                <FormControlLabel
                  value="mixed"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600} color={headingColor}>Mixed Payment</Typography>
                      <Typography variant="body2" sx={{ color: mutedColor }}>
                        Combine wallets + online payment
                      </Typography>
                    </Box>
                  }
                  sx={{ width: '100%', m: 0 }}
                />
              </Paper>
            )}

            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: compactRadius,
                border: '1px solid',
                borderColor: selectedMethod === 'online' ? 'warning.main' : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
                bgcolor: selectedMethod === 'online' ? 'rgba(249, 115, 22, 0.08)' : 'transparent',
              }}
            >
              <FormControlLabel
                value="online"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} color={headingColor}>Online Payment</Typography>
                    <Typography variant="body2" sx={{ color: mutedColor }}>
                      Pay with Stripe or PayPal
                    </Typography>
                  </Box>
                }
                sx={{ width: '100%', m: 0 }}
              />
            </Paper>
          </Stack>
        </RadioGroup>
      </Paper>

      {/* Mixed Payment Breakdown */}
      {selectedMethod === 'mixed' && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius,
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#ffffff',
            border: '1px solid',
            borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
          }}
        >
          <Typography
            variant="subtitle1"
            fontWeight={600}
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center', gap: 1, color: headingColor }}
          >
            <MergeTypeIcon /> Payment Breakdown
          </Typography>

          <Stack spacing={3} mt={2}>
            {canUseRecoveryWallet() && (
              <TextField
                label={`Recovery Wallet (Max: ${formatTokens(recoveryWalletBalance)})`}
                type="number"
                value={paymentBreakdown.recoveryWallet}
                onChange={(e) => updateBreakdownValue('recoveryWallet', e.target.value)}
                InputProps={{ inputProps: { min: 0, max: recoveryWalletBalance } }}
                fullWidth
              />
            )}

            {canUseMainWallet() && (
              <TextField
                label={`Main Wallet (Max: ${formatTokens(mainWalletBalance)})`}
                type="number"
                value={paymentBreakdown.mainWallet}
                onChange={(e) => updateBreakdownValue('mainWallet', e.target.value)}
                InputProps={{ inputProps: { min: 0, max: mainWalletBalance } }}
                fullWidth
              />
            )}

            <TextField
              label="Online Payment"
              type="number"
              value={paymentBreakdown.onlinePayment}
              onChange={(e) => updateBreakdownValue('onlinePayment', e.target.value)}
              InputProps={{ inputProps: { min: 0 } }}
              fullWidth
            />

            <Divider />

            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between">
                <Typography sx={{ color: mutedColor }}>Total:</Typography>
                <Typography fontWeight={700} color={isBreakdownValid() ? 'success.main' : 'error.main'}>
                  {formatTokens(getTotalBreakdown())}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography sx={{ color: mutedColor }}>Required:</Typography>
                <Typography fontWeight={700}>
                  {formatTokens(totalAmount)}
                </Typography>
              </Stack>
              {!isBreakdownValid() && (
                <Typography variant="caption" color="error.main">
                  ⚠️ Breakdown total must equal {formatTokens(totalAmount)}
                </Typography>
              )}
            </Stack>

            <Button
              variant="contained"
              color="secondary"
              onClick={calculateOptimalBreakdown}
              startIcon={<AutoFixHighIcon />}
            >
              Auto-Balance Payment
            </Button>
          </Stack>
        </Paper>
      )}

      {/* Insufficient Funds Warning */}
      {!canAffordWithBothWallets() && selectedMethod !== 'online' && selectedMethod !== 'mixed' && (
        <Alert severity="error" variant="outlined" sx={{ borderRadius: compactRadius }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ color: headingColor }}>Insufficient Funds</Typography>
          <Typography variant="body2" sx={{ color: mutedColor }}>
            You need {formatTokens(totalAmount - (recoveryWalletBalance + mainWalletBalance))} more. 
            Consider using mixed payment with online payment option.
          </Typography>
          <Button
            size="small"
            variant="outlined"
            color="primary"
            onClick={() => navigate('/wallet/topup')}
            sx={{ mt: 1, borderRadius: '25px', fontWeight: 700 }}
          >
            Top Up Wallet
          </Button>
        </Alert>
      )}
    </Stack>
  );
};

export default PaymentOptions;
